import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  const isDesktop = useMediaQuery({ minWidth: 1025 });
  const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isSmallMobile = useMediaQuery({ maxWidth: 480 });
  const isLargeDesktop = useMediaQuery({ minWidth: 1440 });

  // Uniformisation du dégradé pour un look luxueux et constant
  const premiumGradient = 'linear-gradient(135deg, rgba(20, 20, 22, 0.85) 0%, rgba(26, 26, 26, 0.6) 50%, rgba(0, 0, 0, 0.8) 100%)';

  const slides = [
    {
      id: 1,
      image: '/images/L1.webp',
      title: 'Droit des sociétés',
      subtitle: 'Du projet à la croissance, un accompagnement sur mesure.',
      paragraph: 'Nous aidons les entreprises à structurer leur croissance, respecter la loi et éviter des litiges coûteux et imprévus.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services1",
      gradient: premiumGradient,
    },
    {
      id: 2,
      image: '/images/L2.png',
      title: 'Expertise Corporative',
      subtitle: 'Votre sécurité financière commence par une bonne stratégie.',
      paragraph: 'Nos juristes optimisent votre structure d\'entreprise pour garantir conformité, performance et protection.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services2",
      gradient: premiumGradient,
    },
    {
      id: 3,
      image: '/images/L4.jpg',
      title: 'Droit financier',
      subtitle: 'Sécurisation de vos opérations financières',
      paragraph: 'Conseil et représentation pour la structuration de vos financements et la gestion des contentieux bancaires.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services3",
      gradient: premiumGradient,
    },
    {
      id: 4,
      image: '/images/L3.webp',
      title: 'Droit minier',
      subtitle: 'Encadrement juridique de vos exploitations',
      paragraph: 'Expertise dans l\'encadrement juridique des activités minières : permis, fiscalité et négociation contractuelle.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services4",
      gradient: premiumGradient,
    },
    {
      id: 5,
      image: '/images/L8.avif',
      title: 'Environnement & Hydrocarbures',
      subtitle: 'Conformité et exploitation responsable',
      paragraph: 'Nous conseillons les entreprises sur les obligations légales liées à l\'environnement et aux ressources naturelles.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services5",
      gradient: premiumGradient,
    },
    {
      id: 6,
      image: '/images/L9.jpg',
      title: 'Droit des assurances',
      subtitle: 'Protection optimale de vos intérêts',
      paragraph: 'Nous assurons une gestion rigoureuse des contrats d\'assurance et un appui juridique lors de sinistres.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services6",
      gradient: premiumGradient,
    },
    {
      id: 7,
      image: '/images/L10.jpg',
      title: 'Contentieux civil & commercial',
      subtitle: 'Défense rigoureuse devant les juridictions',
      paragraph: 'Nous défendons efficacement vos intérêts devant les juridictions civiles et commerciales pour tous types de litiges.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services7",
      gradient: premiumGradient,
    },
    {
      id: 8,
      image: '/images/L11.webp',
      title: 'Responsabilité sociale',
      subtitle: 'Éthique et développement durable',
      paragraph: 'Nous accompagnons vos actions RSE pour conjuguer performance économique, conformité et durabilité.',
      text: "Découvrir plus",
      lien: "/Services-Infos/Services8",
      gradient: premiumGradient,
    },
  ];

  const getFontSize = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getSpacing = (desktop, tablet, mobile, smallMobile) => {
    if (isSmallMobile) return smallMobile;
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  const getContentWidth = () => {
    if (isSmallMobile) return '95%';
    if (isMobile) return '90%';
    if (isTablet) return '85%';
    return isLargeDesktop ? '55%' : '65%';
  };

  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > 50) {
      if (distance > 0) setActiveIndex((prev) => (prev + 1) % slides.length);
      else setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    const startAutoRotation = () => {
      intervalRef.current = setInterval(() => {
        if (!isHovering) setActiveIndex((prev) => (prev + 1) % slides.length);
      }, 8000);
    };
    startAutoRotation();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [slides.length, isHovering]);

  useEffect(() => {
    if (!isDesktop) return;
    const handleMouseMove = (e) => {
      setMousePos({ 
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2 
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isDesktop]);

  const handleSlideChange = (index) => setActiveIndex(index);
  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);

  const slideVariants = {
    enter: { opacity: 0, scale: 1.05 },
    center: { opacity: 1, scale: 1, transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.8 } },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <div 
      ref={containerRef}
      style={styles.container}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Effets de particules (Poussière dorée élégante) */}
      <div style={styles.particlesContainer}>
        {[...Array(isMobile ? 12 : isDesktop ? 40 : 20)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              ...styles.particle,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: Math.random() * (isMobile ? 1.5 : 2.5) + 1,
              height: Math.random() * (isMobile ? 1.5 : 2.5) + 1,
            }}
            animate={{ y: [0, -40, 0], opacity: [0.1, 0.4, 0.1] }}
            transition={{ duration: Math.random() * 5 + 4, repeat: Infinity, ease: "linear", delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Lumière diffuse de la souris (Beige/Taupe clair) */}
      {isDesktop && (
        <motion.div
          style={styles.mouseLight}
          animate={{ x: mousePos.x * 60, y: mousePos.y * 60 }}
          transition={{ type: "spring", stiffness: 100, damping: 30 }}
        />
      )}

      {/* Glow Orbs (Éclairage de studio subtil) */}
      <motion.div 
        style={{ ...styles.glowOrb1, width: isMobile ? 200 : 500, height: isMobile ? 200 : 500 }}
        animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        style={{ ...styles.glowOrb2, width: isMobile ? 150 : 400, height: isMobile ? 150 : 400 }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Contrôles Desktop */}
      {isDesktop && (
        <>
          <motion.button
            style={styles.navButtonPrev}
            onClick={prevSlide}
            whileHover={{ scale: 1.05, backgroundColor: 'rgba(193, 166, 141, 0.1)', borderColor: '#C1A68D' }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.button>

          <motion.button
            style={styles.navButtonNext}
            onClick={nextSlide}
            whileHover={{ scale: 1.05, backgroundColor: 'rgba(193, 166, 141, 0.1)', borderColor: '#C1A68D' }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.button>
        </>
      )}

      {/* Barre de progression */}
      <div style={{ ...styles.progressContainer, width: isMobile ? '90%' : '60%', bottom: isMobile ? '100px' : '30px' }}>
        <motion.div 
          style={styles.progressBar}
          initial={{ width: '0%' }}
          animate={{ width: isHovering ? '0%' : '100%' }}
          transition={{ duration: 8, ease: "linear", repeat: isHovering ? 0 : Infinity }}
          key={activeIndex}
        />
      </div>

      {/* Contenu principal */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeIndex}
          style={{
            ...styles.slideContainer,
            backgroundImage: `url(${slides[activeIndex].image})`,
            transform: isDesktop ? `translateX(${mousePos.x * 15}px) translateY(${mousePos.y * 15}px)` : 'none',
          }}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          {/* Superpositions */}
          <div style={{ ...styles.overlayGradient, background: slides[activeIndex].gradient }} />
          <div style={styles.noiseOverlay} />

          <div style={{
            ...styles.contentWrapper,
            maxWidth: getContentWidth(),
            paddingLeft: '5%', paddingRight: isMobile ? '5%' : '0',
            alignItems: isMobile ? 'center' : 'flex-start',
            textAlign: isMobile ? 'center' : 'left',
          }}>
            <motion.div
              style={{
                ...styles.glassCard,
                padding: getSpacing('3.5rem', '2.5rem', '2rem', '1.5rem'),
                borderRadius: '6px',
                backdropFilter: isMobile ? 'blur(8px)' : 'blur(12px)',
                marginTop: isMobile ? '50px' : '0',
              }}
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                style={{
                  ...styles.categoryTag,
                  fontSize: getFontSize('0.85rem', '0.8rem', '0.75rem', '0.7rem'),
                  padding: '6px 12px',
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                Expertise Juridique
              </motion.div>

              <motion.h1
                style={{
                  ...styles.title,
                  fontSize: getFontSize('3.8rem', '3rem', '2.2rem', '1.8rem'),
                  lineHeight: 1.1,
                  marginBottom: '1rem',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {slides[activeIndex].title}
              </motion.h1>

              <motion.h2
                style={{
                  ...styles.subtitle,
                  fontSize: getFontSize('1.3rem', '1.1rem', '1rem', '0.9rem'),
                  marginBottom: '1.2rem',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {slides[activeIndex].subtitle}
              </motion.h2>

              <motion.p
                style={{
                  ...styles.paragraph,
                  fontSize: getFontSize('1.05rem', '0.95rem', '0.9rem', '0.85rem'),
                  marginBottom: '2.5rem',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {slides[activeIndex].paragraph}
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
                <Link to={slides[activeIndex].lien} style={styles.buttonLink}>
                  <motion.button
                    style={{
                      ...styles.actionButton,
                      padding: getSpacing('1rem 2.5rem', '0.9rem 2rem', '0.8rem 1.8rem', '0.8rem 1.5rem'),
                      fontSize: getFontSize('1rem', '0.95rem', '0.9rem', '0.85rem'),
                      width: isMobile ? '100%' : 'auto',
                    }}
                    whileHover={{ backgroundColor: '#A88D73', scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{slides[activeIndex].text}</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Indicateurs de points */}
            <div style={{ ...styles.slideIndicators, justifyContent: isMobile ? 'center' : 'flex-start', marginTop: '2rem' }}>
              {slides.map((_, index) => (
                <motion.button
                  key={index}
                  style={{
                    ...styles.slideIndicator,
                    width: index === activeIndex ? '24px' : '8px',
                    height: '8px',
                    margin: '0 5px',
                    backgroundColor: index === activeIndex ? '#C1A68D' : 'rgba(193, 166, 141, 0.3)',
                    borderRadius: '4px',
                  }}
                  onClick={() => handleSlideChange(index)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Compteur et Contrôles du bas */}
      <div style={{
        ...styles.bottomControls,
        bottom: isMobile ? '150px' : '40px',
        padding: getSpacing('0 3rem', '0 2.5rem', '0 1.5rem', '0 1rem'),
        justifyContent: 'space-between',
      }}>
        <div style={styles.slideCounter}>
          <span style={styles.current}>{String(activeIndex + 1).padStart(2, '0')}</span>
          <span style={styles.separator}>—</span>
          <span style={styles.total}>{String(slides.length).padStart(2, '0')}</span>
        </div>
        
        {(isMobile || isTablet) && (
          <div style={styles.mobileNavButtons}>
            <motion.button onClick={prevSlide} style={styles.mobileNavBtn} whileTap={{ scale: 0.9 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.button>
            <motion.button onClick={nextSlide} style={{ ...styles.mobileNavBtn, marginLeft: '10px' }} whileTap={{ scale: 0.9 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.button>
          </div>
        )}
      </div>

      {isMobile && (
        <motion.div style={styles.swipeHint} initial={{ opacity: 0 }} animate={{ opacity: 0.7 }} transition={{ delay: 1 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M7 13L12 18L17 13M7 6L12 11L17 6" stroke="#D4C5B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ fontSize: '0.75rem', marginLeft: '6px', color: '#D4C5B0', letterSpacing: '0.5px' }}>Glisser pour naviguer</span>
        </motion.div>
      )}
    </div>
  );
};

const styles = {
  container: { position: 'relative', width: '100%', height: '100vh', minHeight: '650px', overflow: 'hidden', background: '#0A0A0A' },
  
  particlesContainer: { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, overflow: 'hidden' },
  particle: { position: 'absolute', borderRadius: '50%', background: '#C1A68D', filter: 'blur(1px)' },
  
  mouseLight: {
    position: 'absolute', width: 300, height: 300, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(193,166,141,0.08), rgba(0,0,0,0))', pointerEvents: 'none', zIndex: 2, mixBlendMode: 'screen'
  },
  
  glowOrb1: { position: 'absolute', top: '-10%', right: '-5%', background: 'radial-gradient(circle, rgba(193,166,141,0.12), transparent 70%)', filter: 'blur(60px)', zIndex: 0 },
  glowOrb2: { position: 'absolute', bottom: '-5%', left: '-5%', background: 'radial-gradient(circle, rgba(212,197,176,0.08), transparent 70%)', filter: 'blur(60px)', zIndex: 0 },

  slideContainer: { position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 },
  overlayGradient: { position: 'absolute', inset: 0, zIndex: 4 },
  noiseOverlay: { position: 'absolute', inset: 0, backgroundImage: "url('https://www.transparenttextures.com/patterns/stardust.png')", opacity: 0.15, mixBlendMode: 'overlay', pointerEvents: 'none', zIndex: 5 },

  contentWrapper: { position: 'relative', zIndex: 6, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  
  glassCard: {
    background: 'rgba(26, 26, 26, 0.45)', // Dark gray translucide
    border: '1px solid rgba(193, 166, 141, 0.15)', // Bordure beige discrète
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
  },

  categoryTag: {
    border: '1px solid #C1A68D',
    color: '#C1A68D',
    background: 'rgba(193, 166, 141, 0.05)',
    display: 'inline-block',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontWeight: 500,
  },

  title: { fontWeight: 700, color: '#F5F0E6', textTransform: 'uppercase', letterSpacing: '2px', textShadow: '0 4px 15px rgba(0,0,0,0.6)' },
  subtitle: { fontWeight: 400, color: '#C1A68D', letterSpacing: '0.5px' },
  paragraph: { color: '#D4C5B0', lineHeight: 1.6, fontWeight: 300 },

  progressContainer: { position: 'absolute', left: '50%', transform: 'translateX(-50%)', height: '2px', background: 'rgba(193, 166, 141, 0.2)', zIndex: 10 },
  progressBar: { height: '100%', background: '#C1A68D' },

  navButtonPrev: { position: 'absolute', top: '50%', left: '3%', transform: 'translateY(-50%)', background: 'transparent', border: '1px solid rgba(193, 166, 141, 0.3)', borderRadius: '50%', width: '50px', height: '50px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11, backdropFilter: 'blur(4px)', transition: 'all 0.3s ease' },
  navButtonNext: { position: 'absolute', top: '50%', right: '3%', transform: 'translateY(-50%)', background: 'transparent', border: '1px solid rgba(193, 166, 141, 0.3)', borderRadius: '50%', width: '50px', height: '50px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11, backdropFilter: 'blur(4px)', transition: 'all 0.3s ease' },

  buttonLink: { textDecoration: 'none', display: 'inline-block' },
  actionButton: {
    background: '#C1A68D',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
  },

  slideIndicators: { display: 'flex', alignItems: 'center', zIndex: 10 },
  slideIndicator: { border: 'none', cursor: 'pointer', transition: 'all 0.3s ease' },

  bottomControls: { position: 'absolute', left: 0, right: 0, display: 'flex', alignItems: 'center', zIndex: 10 },
  slideCounter: { display: 'flex', alignItems: 'center', color: '#D4C5B0', fontFamily: 'system-ui, -apple-system, sans-serif' },
  current: { fontWeight: 600, fontSize: '1.2rem', color: '#F5F0E6' },
  separator: { margin: '0 10px', opacity: 0.5 },
  total: { opacity: 0.6, fontSize: '1rem' },

  mobileNavButtons: { display: 'flex', alignItems: 'center' },
  mobileNavBtn: { background: 'transparent', border: '1px solid rgba(193, 166, 141, 0.3)', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' },

  swipeHint: { position: 'absolute', bottom: '30px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, background: 'rgba(26,26,26,0.6)', padding: '6px 14px', borderRadius: '20px', border: '1px solid rgba(193,166,141,0.2)', backdropFilter: 'blur(5px)' },
};

export default Hero;